/**
 * NoteConfigManager - Manages the active annotation configuration.
 *
 * Controls the active style applied to new entities (color, layer, line weight, etc.)
 * and layer configurations from legends.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import {
  type DrawingNoteConfig,
  type DrawingLayerConfig,
  DEFAULT_NOTE_CONFIG,
  normalizeColor,
} from "../types/NoteConfig";
import { applyEntityProps } from "../entities/properties";
import {
  ensureLayerExists,
  migrateForegroundLayerColor,
  updateLayer,
} from "../layers/utils";

export class NoteConfigManager {
  constructor(private viewer: DRDrawingViewer) {}

  /**
   * Get the current active note configuration.
   */
  get active(): DrawingNoteConfig {
    return this.viewer.activeNoteConfig ?? DEFAULT_NOTE_CONFIG;
  }

  /**
   * Set the active note configuration.
   * New entities will use these properties.
   * @param config The note configuration to activate
   */
  setActive(config: DrawingNoteConfig): void {
    // NoteMenuState.computeNoteConfig a déjà résolu layerId -> layername (nécessaire pour
    // le payload de drag-and-drop du canvas, qui ne passe jamais par setActive). Ici, on a
    // de toute façon besoin du Layer complet pour son color/lineweight/linetype : on le
    // recherche par id (source de vérité stable) plutôt que de refaire confiance au nom
    // déjà résolu, ce qui évite toute fragilité si jamais les deux listes de calques
    // (activeLegend.layer_configs côté NoteMenuState/DraggerMenuState, activeLayerConfigs
    // ici) venaient à diverger.
    const layerConfig = config.props.layerId
      ? this.viewer.activeLayerConfigs.find(
          (l) => l.id === config.props.layerId,
        )
      : undefined;
    const layerName = layerConfig?.name;

    this.viewer.activeNoteConfig = layerName
      ? { ...config, props: { ...config.props, layername: layerName } }
      : config;

    // Ensure the layer exists if specified
    if (layerName && this.viewer.database) {
      const color = normalizeColor(layerConfig?.color);
      ensureLayerExists(
        this.viewer.module,
        this.viewer.database,
        layerName,
        color,
        layerConfig?.lineweight,
        layerConfig?.linetype,
      );
      // ensureLayerExists only sets color/lineweight/linetype at creation time — if the
      // layer already existed in this drawing (e.g. from before the legend was edited,
      // or created without linetype), its properties are never refreshed otherwise. This
      // was the cause of a note using linetype "ByLayer" always rendering Continuous even
      // after setting the layer's linetype to "Dashed" in the configurator.
      updateLayer(
        this.viewer.module,
        this.viewer.database,
        layerName,
        color,
        layerConfig?.lineweight,
        layerConfig?.linetype,
      );
    }
  }

  /**
   * Get the active layer configurations.
   */
  get layerConfigs(): DrawingLayerConfig[] {
    return this.viewer.activeLayerConfigs ?? [];
  }

  /**
   * Set the layer configurations (from legend).
   * These define how layers should be created when a NoteConfig is activated.
   */
  setLayerConfigs(configs: DrawingLayerConfig[]): void {
    this.viewer.activeLayerConfigs = configs;
  }

  /**
   * Plan background changed: the legend layers already in the drawing that
   * are still in TrueColor pure black or white move to ACI 7, so they and
   * their ByLayer strokes follow the new background (cf.
   * migrateForegroundLayerColor). Out of the history, like activating a note;
   * missing layers are not created, other colors are left alone.
   */
  refreshForegroundLayers(): void {
    if (!this.viewer.database) return;
    for (const layer of this.layerConfigs) {
      migrateForegroundLayerColor(
        this.viewer.module,
        this.viewer.database,
        layer.name,
      );
    }
  }

  /**
   * Apply the active note config properties to an entity.
   * This is called by draggers after creating entities.
   * @param entityId The entity to apply properties to
   */
  applyToEntity(entityId: DrawingModule.OdDbObjectId): void {
    if (!this.viewer.database) return;
    const config = this.viewer.activeNoteConfig;
    applyEntityProps(
      this.viewer.module,
      this.viewer.database,
      entityId,
      config.props,
    );
  }

  /**
   * Get the current label to display on shapes.
   */
  get label(): string | undefined {
    return this.viewer.activeNoteConfig?.label;
  }

  /**
   * Get the current hatch configuration.
   */
  get hatch(): DrawingNoteConfig["hatch"] {
    return this.viewer.activeNoteConfig?.hatch;
  }

  /**
   * Reset to default note configuration.
   */
  reset(): void {
    this.viewer.activeNoteConfig = DEFAULT_NOTE_CONFIG;
  }
}
